// Foundations
import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

// Services
import { MyProfileService } from '../../../services/my-profile.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { GeoCountry, PhoneType } from 'src/app/services/graphql.service';

// Models
import { ContactDetails } from '../../../models/contact-details.model';
import { BinaryChoice } from '../../../models/binary-choice.model';

// Components
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

const PHONE_REGEX = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
const DEFAULT_GEO_COUNTRY: Partial<GeoCountry> = {
  id: '228',
  name: 'United States of America',
  countryCallingCode: 1,
};

@Component({
  selector: 'app-contact-details-form',
  templateUrl: './contact-details-form.component.html',
  styleUrls: ['./contact-details-form.component.scss'],
})
export class ContactDetailsFormComponent implements OnInit, OnChanges {
  @Input() inputFormData: ContactDetails;
  @Input() isLastEmergency: boolean;
  @Input() contactDetailsForm: FormGroup;
  @Input() displaySelectAsEmergency = false;
  @Input() isContactDetail = true;

  relations = ['Spouse', 'Partner', 'Immediate Family'];
  isRelated = false;
  displayRelated = 'N';
  yesNoList: BinaryChoice[] = [
    { text: 'Yes', value: true },
    { text: 'No', value: false },
  ];

  constructor(
    private fb: FormBuilder,
    public myProfileService: MyProfileService,
    private _analytics: AnalyticsService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef
  ) {
    this.contactDetailsForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: [
        '',
        { validators: [Validators.required, Validators.pattern(EMAIL_REGEX)] },
      ],
      phones: this.fb.array([]),
      isEmergencyContact: [false],
      isRelated: [false],
      relationship: [this.relations[0]],
    });

    this.addAdditionalPhone();
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit() {
    if (this.inputFormData) {
      if (this.isRelated) {
        this.displayRelated = 'Y';
      }

      const {
        firstName,
        lastName,
        phones,
        isEmergencyContact,
        isRelated,
        relationship,
      } = this.inputFormData;
      this.contactDetailsForm.patchValue({
        firstName,
        lastName,
        isEmergencyContact,
        isRelated,
        relationship,
      });
      this.phoneArray.clear();
      if (phones?.length) {
        phones.forEach((phone) => {
          const { id, phoneNumber, type } = phone;
          this.phoneArray.push(
            this.fb.group({
              id,
              phoneNumber: [
                phoneNumber,
                [Validators.required, Validators.pattern(PHONE_REGEX)],
              ],
              phoneType: [
                this.getPhoneTypeObjByEnum(type),
                Validators.required,
              ],
            })
          );
        });
      }
    }

    if (this.isContactDetail) {
      // Contact Details tab
      this.contactDetailsForm.get('email').valueChanges.subscribe((res) => {
        this.contactDetailsForm.patchValue(
          { email: this.contactDetailsForm.get('email').value.toLowerCase() },
          { emitEvent: false }
        );
      });
    } else {
      // Other Contacts tab

      // email not required
      const emailCtrl = this.contactDetailsForm.get('email');
      emailCtrl.clearValidators();
      emailCtrl.updateValueAndValidity();

      // isRelated required
      const isRelatedCtrl = this.contactDetailsForm.get('isRelated');
      isRelatedCtrl.setValidators(Validators.required);
      isRelatedCtrl.updateValueAndValidity();

      // if isRelated equals true, make relationship required
      const relationshipCtrl = this.contactDetailsForm.get('relationship');
      isRelatedCtrl.valueChanges.subscribe((isRelatedValue) => {
        if (isRelatedValue) {
          relationshipCtrl.setValidators(Validators.required);
        } else {
          relationshipCtrl.clearValidators();
        }
        relationshipCtrl.updateValueAndValidity();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isContactDetail && changes.isLastEmergency?.currentValue) {
      this.contactDetailsForm.controls.isEmergencyContact.disable();
      this.cdRef.detectChanges();
    }
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  getForm() {
    return this.contactDetailsForm;
  }

  get f() {
    return this.contactDetailsForm.controls;
  }

  get phoneArray(): FormArray {
    return this.contactDetailsForm.get('phones') as FormArray;
  }

  // for mat-select to compare objects as option values
  compareFn(optionOne: PhoneType, optionTwo: PhoneType): boolean {
    return optionOne.id === optionTwo.id;
  }

  addAdditionalPhone() {
    this.phoneArray.push(this.createPhone());
  }

  createPhone() {
    return this.fb.group({
      id: null,
      geoCountry: DEFAULT_GEO_COUNTRY,
      phoneNumber: ['', [Validators.required, Validators.pattern(PHONE_REGEX)]],
      phoneType: this.myProfileService.phoneTypeList[0],
      isPrimary: this.phoneArray.length === 0,
      isVerified: false,
      marketingSmsOptIn: false,
    });
  }

  deletePhone(index) {
    this.phoneArray.removeAt(index);
  }

  toggleEmergency() {
    this._analytics.logButtonTap('myProfile_selectAsEmergencyContactToggle');

    // if this is an emergency and there is only one emergency prevent toggle and show message
    if (this.isLastEmergency) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'general-dialog',
        data: {
          title: 'OOPS!',
          content: `You must have one emergency contact at all times.`,
          okText: 'OK',
          okFn: () => {
            dialogRef.close();
          },
        },
        autoFocus: false,
        disableClose: true,
      });
    }
  }

  toggleRelated() {
    this.isRelated = !this.isRelated;
  }

  setRelated(relatedValue: string) {
    this.isRelated = relatedValue === 'Y';
  }

  // Private methods
  // ------------------------------------------------------------------------------------

  private getPhoneTypeObjByEnum(enumValue: string): PhoneType {
    // convert camelCase string to space-separated string
    const phoneTypeName = enumValue.replace(/([a-z])([A-Z])/g, '$1 $2');
    return this.myProfileService.phoneTypeList.find(
      (phoneType) => phoneType.type === phoneTypeName
    );
  }
}
